import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Header.module.scss";
import { useHistory } from "react-router-dom";
import { eventDetailPath, jobDetailPath, PAGES } from "../../routes/PAGES";
import { useCommunity, useCurrentUser, useUserProfile, DEFAULT_LANG_CHOICES, isMultilingualCommunity } from "../../context/community";
import LoginButton from "../LoginButton/LoginButton";
import { applyCommunityLanguage } from "./components/LangSelector";
import { DualTierWorkspaceNav } from "../NavBarTemplates/templates/DualTierWorkspaceNav";
import { EventOnlyOnboardingModal, useNavbarLinks } from "../NavBar/NavBar";
import Modal from "components/Modal/Modal";
import Button from "components/Button/Button";
import { isConnectLandingEnabled } from "pages/Welcome/components/ConnectLanding/parseConnectLandingConfig";
import { isKrasavaLandingEnabled } from "pages/Welcome/components/KrasavaLanding/parseKrasavaLandingConfig";
import LandingHeader from "./components/LandingHeader";
import LangService from "../../services/LangService";
import { getToken, isAdmin, resetUserData } from "../../services/auth";
import { navBarClass, navIconNameForTo } from "../NavBarTemplates/navIcons";
import { voleeApi } from "api/VoleeApi";
import { resolveCommunityLogo } from "services/themePalette";
import { enrichNotification, formatTimeAgo } from "components/Notifications/NotificationHelper";

const SHOULD_HIDE_LOGO = [
  PAGES.LOGIN,
  PAGES.REGISTRATION
];

const COMMAND_SEARCH_PAGE_SIZE = 30;
// Members are paged at 20 on the server. Prefetch until the first screen is filled; scroll loads more via hasMore.

const toCommandMember = (person) => ({
  id: person.id,
  to: person.slug ? `/u/${person.slug}` : undefined,
  label: [person.firstName, person.lastName].filter(Boolean).join(' ') || person.name || person.slug,
  image: person.avatarUrl,
  subtitle: person.company || person.position,
});

const toCommandEvent = (event) => ({
  id: event.id,
  to: eventDetailPath(event.slug || event.id),
  label: event.title,
  image: event.imageUrl,
});

const toCommandJob = (job) => ({
  id: job.id,
  to: job.slug ? jobDetailPath(job.slug) : undefined,
  label: job.title,
  image: job.logoUrl,
  subtitle: job.organizationName,
});

const notificationKind = (item, to) => {
  const type = String(item?.type || '').toUpperCase();
  const path = String(to || '');
  if (type.includes('EVENT') || path.startsWith('/event')) return 'event';
  if (type.includes('JOB') || type.includes('VACANCY') || type.includes('CAREER') || path.startsWith('/job')) return 'job';
  return 'person';
};

const mapNavNotification = (item, langMap, unread = false) => {
  const enriched = enrichNotification(item, langMap);
  const to = enriched.deepLinkUrl || item.deepLinkUrl;
  return {
    id: item.id,
    kind: notificationKind(item, to),
    title: enriched.title || enriched.content || item.content || '',
    time: item.createdAt ? formatTimeAgo(item.createdAt, LangService.lang.toLowerCase()) : '',
    unread: unread || item.status === 'NEW',
    to,
    name: item.relatedUser?.name || item.relatedUser?.firstName || item.relatedEvent?.title,
    image: item.relatedUser?.avatarUrl || item.relatedEvent?.imageUrl || item.imgUrl,
  };
};

const NOTIFICATION_PAGE_SIZE = 10;

const flattenNotifications = (data) => {
  const langMap = LangService.map.components.notifications || {};
  const raw = Array.isArray(data?.items)
    ? data.items
    : [...(data?.new || []), ...(data?.seen || []), ...(data?.actions || [])];
  return raw.map((item) => mapNavNotification(item, langMap, item.status === 'NEW'));
};

const useHeaderNotifications = (enabled) => {
  const [notifications, setNotifications] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const clearingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const notificationsRef = useRef([]);
  notificationsRef.current = notifications;

  const applyPage = (data, { append = false } = {}) => {
    const next = flattenNotifications(data);
    setNotifications((prev) => {
      if (!append) {
        return next;
      }
      const seen = new Set(prev.map((item) => item.id));
      return [...prev, ...next.filter((item) => !seen.has(item.id))];
    });
    if (typeof data?.unreadCount === 'number') {
      setUnreadCount(data.unreadCount);
    } else {
      setUnreadCount(next.filter((item) => item.unread).length);
    }
    if (typeof data?.hasMore === 'boolean') {
      setHasMore(data.hasMore);
    } else {
      setHasMore(next.length === NOTIFICATION_PAGE_SIZE);
    }
  };

  useEffect(() => {
    if (!enabled) {
      setNotifications([]);
      setHasMore(false);
      setUnreadCount(0);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      if (clearingRef.current || loadingMoreRef.current) {
        return;
      }
      try {
        const limit = Math.max(NOTIFICATION_PAGE_SIZE, notificationsRef.current.length || NOTIFICATION_PAGE_SIZE);
        const data = await voleeApi.general.notifications.get({ offset: 0, limit });
        if (!cancelled && !clearingRef.current) {
          applyPage(data);
        }
      } catch (e) {
        if (!cancelled && !clearingRef.current) {
          setNotifications([]);
          setHasMore(false);
          setUnreadCount(0);
        }
      }
    };
    load();
    const interval = setInterval(load, 15000);
    window.addEventListener('profile_updated', load);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('profile_updated', load);
    };
  }, [enabled]);

  const dismiss = (item) => {
    if (!item?.id) {
      return;
    }
    setNotifications((prev) => prev.filter((entry) => entry.id !== item.id));
    if (item.unread) {
      setUnreadCount((count) => Math.max(0, count - 1));
    }
    voleeApi.general.notifications.dismiss([item.id]);
  };

  const loadMore = async () => {
    if (!hasMore || loadingMoreRef.current || clearingRef.current) {
      return;
    }
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const data = await voleeApi.general.notifications.get({
        offset: notificationsRef.current.length,
        limit: NOTIFICATION_PAGE_SIZE,
      });
      applyPage(data, { append: true });
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const clearAll = async () => {
    clearingRef.current = true;
    setNotifications([]);
    setHasMore(false);
    setUnreadCount(0);
    try {
      await voleeApi.general.notifications.dismissAll();
    } finally {
      clearingRef.current = false;
    }
  };

  return { notifications, unreadCount, hasMore, loadingMore, dismiss, loadMore, clearAll };
};

const Header = () => {
  const history = useHistory();
  const { userId, avatarUrl, slug, username, firstName, lastName } = useCurrentUser();
  const userProfile = useUserProfile();
  const { community, resetData, setCommunity, themeMode, loading: communityLoading } = useCommunity();
  const { domain, isTimCommunity } = community;
  const rawLangChoices = community?.langChoices;
  const hasLangSelector = isMultilingualCommunity(rawLangChoices);
  const langChoices = hasLangSelector ? rawLangChoices : DEFAULT_LANG_CHOICES;
  const isHidden = SHOULD_HIDE_LOGO.includes(history.location.pathname);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const isPhystechCommunity = community?.domain === "phystech4ever";
  const configuredSupportUrl = /^https?:\/\//i.test(String(community?.supportUrl || '').trim())
    ? String(community.supportUrl).trim()
    : '';
  const { notifications, unreadCount, hasMore, loadingMore, dismiss, loadMore, clearAll } = useHeaderNotifications(Boolean(userId));
  const {
    links,
    visible,
    showOnboardingModal,
    setShowOnboardingModal,
    slug: navSlug,
  } = useNavbarLinks();

  const profileSlug = slug || navSlug;
  const items = useMemo(() => links
    .filter((link) => link.to !== PAGES.PROFILE && link.to !== `/u/${profileSlug}`)
    .map((link) => ({
      id: link.to,
      to: link.to,
      label: link.text,
      icon: navIconNameForTo(link.to, { isHelp: link.isHelp }),
      badge: link.badge,
      onClick: link.onClick,
      restrictedForEventOnly: link.restrictedForEventOnly,
      isHelp: link.isHelp,
      isActive: link.isActive,
    })), [links, profileSlug]);

  const canSearchMembers = items.some((item) => item.to === PAGES.PARTICIPANTS);
  const canSearchEvents = items.some((item) => item.to === PAGES.EVENT_LIST);
  const canSearchJobs = items.some((item) => item.to === PAGES.VACANCY_LIST);
  const communityId = community?.id;

  const handleCommandSearch = useCallback(async (query, cursor = {}) => {
    const q = query.trim();
    if (!communityId || q.length < 2) {
      return { members: [], events: [], jobs: [] };
    }

    const fetchMembers = cursor.members !== false && canSearchMembers;
    const fetchEvents = cursor.events !== false && canSearchEvents;
    const fetchJobs = cursor.jobs !== false && canSearchJobs;
    const singlePage = Boolean(cursor.singlePage);
    const emptyPage = { list: [], hasMore: false };

    const eventsPromise = fetchEvents
      ? voleeApi.event.search(communityId, q).catch(() => [])
      : Promise.resolve([]);
    const jobsPage = cursor.jobsPage ?? 0;
    const jobsPromise = fetchJobs
      ? voleeApi.vacancy.list({
        communityId,
        search: q,
        page: jobsPage,
        size: COMMAND_SEARCH_PAGE_SIZE,
      }).catch(() => emptyPage)
      : Promise.resolve(emptyPage);

    let members = [];
    let membersHasMore = false;
    let membersPage = cursor.membersPage ?? 0;
    let membersError = false;
    if (fetchMembers) {
      try {
        do {
          const res = await voleeApi.community.participants(communityId, membersPage, q);
          const batch = res?.list || [];
          members = members.concat(batch);
          membersHasMore = Boolean(res?.hasMore) && batch.length > 0;
          membersPage += 1;
        } while (
          !singlePage
          && membersHasMore
          && members.length < COMMAND_SEARCH_PAGE_SIZE
        );
      } catch (error) {
        console.warn(error);
        members = [];
        membersHasMore = false;
        membersError = true;
      }
    }

    const [eventsRes, jobsRes] = await Promise.all([eventsPromise, jobsPromise]);
    const jobs = jobsRes?.list || [];

    return {
      members: members.map(toCommandMember).filter((item) => item.to),
      membersHasMore,
      membersNextPage: membersPage,
      membersError,
      events: (Array.isArray(eventsRes) ? eventsRes : []).map(toCommandEvent),
      jobs: jobs.map(toCommandJob).filter((item) => item.to),
      jobsHasMore: Boolean(jobsRes?.hasMore),
      jobsNextPage: jobsPage + 1,
    };
  }, [canSearchEvents, canSearchJobs, canSearchMembers, communityId]);

  if (isTimCommunity && history.location.pathname === PAGES.WELCOME) {
    return <LandingHeader />;
  }

  if ((isConnectLandingEnabled(community) || isKrasavaLandingEnabled(community)) && history.location.pathname === PAGES.WELCOME) {
    return null;
  }

  const profileName = [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ');
  const currentUserName = [firstName, lastName].filter(Boolean).join(' ');
  // Admins skip profile load, so the name has to come from current-user before the slug.
  const userName = profileName || currentUserName || userProfile?.name || profileSlug || '';
  const login = userProfile?.email
    || (Array.isArray(userProfile?.contacts)
      ? userProfile.contacts.find((contact) => contact?.contactType === 'EMAIL')?.value
      : '')
    || username
    || '';
  const drawerUser = userId ? {
    name: userName,
    email: login,
    avatar: avatarUrl,
    slug: profileSlug,
    initials: userName.slice(0, 2).toUpperCase() || 'U',
    isAdmin: isAdmin(),
  } : null;

  const handleNavigate = (to) => {
    if (!to || to === '#') {
      return;
    }
    if (String(to).startsWith('http')) {
      window.open(to, '_blank', 'noopener,noreferrer');
      return;
    }
    if (to === PAGES.WELCOME) {
      gtag('event', 'header_logo_click');
    }
    history.push(to);
  };

  const handleItemClick = (item) => {
    if (item?.onClick) {
      item.onClick();
      return;
    }
    if (item?.restrictedForEventOnly) {
      setShowOnboardingModal(true);
      return;
    }
    if (!item?.to || item.to === '#') {
      return;
    }
    gtag('event', `navbar_to_${String(item.to).replace('/', '')}`, {
      from: history.location.pathname,
      to: item.to,
    });
    history.push(item.to);
  };

  const handleLogout = () => {
    if (userId) {
      gtag('event', 'dropdown_avatar_logout_click');
      // Leave the page you signed out from behind. It is not a deeplink.
      sessionStorage.removeItem('redirectToAfterLogin');
      resetUserData();
      resetData();
    }
    history.push(PAGES.WELCOME);
  };

  const communityLogoUrl = resolveCommunityLogo({
    mode: themeMode,
    logoUrl: community?.logoUrl,
    darkLogoUrl: community?.darkLogoUrl,
  });

  return (
    <>
      <DualTierWorkspaceNav
        items={items}
        activePath={history.location.pathname}
        user={drawerUser}
        community={{
          name: community?.title || community?.domain || 'Joinee',
          logoUrl: communityLogoUrl,
        }}
        onNavigate={handleNavigate}
        onSearch={handleCommandSearch}
        searchScopes={{
          members: canSearchMembers,
          events: canSearchEvents,
          jobs: canSearchJobs,
        }}
        onItemClick={handleItemClick}
        onLogout={handleLogout}
        surfaceClassName={history.location.pathname === PAGES.WELCOME ? undefined : navBarClass}
        hideLogo={isHidden}
        logoPending={communityLoading === true && !communityLogoUrl}
        hideSearch={!visible}
        logoTo={PAGES.WELCOME}
        languages={hasLangSelector ? langChoices.map((code) => ({ code })) : undefined}
        selectedLanguage={String(LangService.lang || 'EN').toUpperCase()}
        onSelectLanguage={(code) => applyCommunityLanguage(code, { userId, community, setCommunity })}
        languageSlot={hasLangSelector ? undefined : null}
        showSupport={Boolean(configuredSupportUrl) || isPhystechCommunity}
        onSupport={() => {
          if (configuredSupportUrl) {
            handleNavigate(configuredSupportUrl);
            return;
          }
          setIsSupportOpen(true);
        }}
        notifications={notifications}
        unreadNotifications={unreadCount}
        hasMoreNotifications={hasMore}
        loadingMoreNotifications={loadingMore}
        onNotificationDismiss={dismiss}
        onNotificationsLoadMore={loadMore}
        onNotificationsClear={clearAll}
        notificationsSlot={userId ? undefined : null}
        userSlot={userId ? undefined : (getToken() ? <span className="inline-block h-9 w-9 animate-pulse rounded-full bg-muted" data-testid="header-session-pending" aria-hidden="true" /> : <LoginButton />)}
      />
      {showOnboardingModal && (
        <EventOnlyOnboardingModal slug={profileSlug} onClose={() => setShowOnboardingModal(false)} />
      )}
      {isPhystechCommunity && !configuredSupportUrl && (
        <Modal
          isOpen={isSupportOpen}
          onClose={() => setIsSupportOpen(false)}
          hasCrossButton
          domain={domain}
        >
          <div className={styles.supportModal}>
            <h3>{LangService.map.components.header.donate}</h3>
            <p>{LangService.map.components.header.supportText}</p>
            <Button
              active
              wide
              onClick={() => {
                window.open('https://buy.stripe.com/14k6rCat7bB97du5kl', '_blank', 'noopener,noreferrer');
                setIsSupportOpen(false);
              }}
            >
              {LangService.map.components.header.donate}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Header;
